/**
 * `2j` — Contratos & aditivos.
 *
 * 🔴 **A tela inteira existe por causa da BR-21.** Um aditivo redefine os
 * parâmetros e vale **da data dele em diante**; o passado continua calculado
 * pelos valores pactuados à época. Duas consequências, e ambas são estruturais e
 * não decorativas:
 *
 *  1. `salvar parâmetros` e `anexar aditivo` são ações SEPARADAS, com mutations
 *     separadas. Editar corrige o que foi digitado errado; repactuar cria uma
 *     linha nova. Se as duas fossem a mesma, alguém repactuaria trocando a PTAX
 *     no formulário — e reescreveria a história do cálculo sem perceber.
 *  2. A lista de aditivos é mostrada em ordem cronológica **como o servidor
 *     mandou**, sem reordenar aqui. Resolver "quais parâmetros valiam em junho"
 *     é caminhar essa lista; uma tela que inventasse a própria ordenação
 *     acertaria até o dia em que errasse em silêncio.
 *
 * 🔒 **BR-04 — a PTAX é digitada, nunca buscada.** Não há e não pode haver botão
 * de "consultar cotação" nesta tela. Desde 17/09/2026 ela é a variável
 * `cotacao_ptax` do conjunto de parâmetros, e não mais um campo com nome próprio.
 *
 * **Variáveis e fórmula são por conjunto de parâmetros** (decidido por Fábio em
 * 17/09/2026): contrato e cada aditivo carregam as suas, e o mesmo
 * `EditorDeParametros` serve aos três formulários. A fórmula é validada aqui para
 * dizer o que está errado, e de novo no servidor, que é quem decide.
 *
 * 🔒 **O Cliente vê e não edita** (fechado em 12/08). Isso não é um caso especial
 * no código: sai de `usePermission('orcamento.contratos').editar`, que vem do
 * `nivel: 'leitura'` do grupo Cliente.
 *
 * ⚠️ **Campos extras do broker Serpro: EM ABERTO (§9).** Eduardo disse "se for
 * Serpro, ele abre outras possibilidades" e nunca disse quais. A tela avisa que
 * faltam, em vez de inventar campos que seriam jogados fora.
 */
import { useState } from 'react'

import {
  TAG_COTACAO_PTAX,
  TAG_DESCONTO_AWS,
  valorDaVariavel,
  type Contrato,
  type VariavelDeContrato,
} from '@/api/contracts/contrato'
import {
  useAditivos,
  useContratos,
  useCriarAditivo,
  useCriarContrato,
  useSalvarContrato,
} from '@/api/queries/orcamento'
import {
  EsqueletoDeFormulario,
  EsqueletoDeLista,
  RegiaoDeCarregamento,
} from '@/components/esqueleto'
import { EstadoDeErro, EstadoVazio } from '@/components/estados'
import { Badge, Botao, Campo, Entrada } from '@/components/primitivos'
import { formatarData, formatarPercentual } from '@/format'
import { usePermission } from '@/permissions/usePermission'
import { useClienteAtual } from '@/tenant/useClienteAtual'

import { EditorDeParametros } from '../EditorDeParametros'
import { estadoInicial, resolverParametros, type EstadoDeParametros } from '../estadoDoEditor'

/**
 * VIGENTE × ENCERRADO sai das datas do próprio contrato.
 *
 * É comparação de datas, e não conversão de valor — por isso pode acontecer
 * aqui sem esbarrar em BR-21. O que NÃO pode acontecer no cliente é decidir
 * quais parâmetros valiam num período; isso continua sendo do servidor.
 */
function vigente(contrato: Contrato, hoje = new Date().toISOString().slice(0, 10)): boolean {
  return contrato.vigenciaInicio <= hoje && hoje <= contrato.vigenciaFim
}

/** Os campos que o formulário desenha, como texto — o estado do `<input>`. */
type Rascunho = {
  broker: string
  quantidadeCsbs: string
  vigenciaInicio: string
  vigenciaFim: string
  mesFiscalDiaInicio: string
}

function paraRascunho(contrato: Contrato): Rascunho {
  return {
    broker: contrato.broker,
    quantidadeCsbs: String(contrato.quantidadeCsbs),
    vigenciaInicio: contrato.vigenciaInicio,
    vigenciaFim: contrato.vigenciaFim,
    mesFiscalDiaInicio: String(contrato.mesFiscalDiaInicio),
  }
}

/**
 * `PTAX 5,42 · desconto AWS 12%` — LIDOS DAS VARIÁVEIS PELA TAG (`cotacao_ptax`,
 * `desconto_aws`). Um conjunto sem a tag, ou com o valor deixado ao servidor,
 * simplesmente omite a parte: "PTAX 0" afirmaria uma cotação que ninguém pactuou.
 */
function resumoDeConstantes(variaveis: readonly VariavelDeContrato[]): string {
  const ptax = valorDaVariavel(variaveis, TAG_COTACAO_PTAX)
  const desconto = valorDaVariavel(variaveis, TAG_DESCONTO_AWS)
  return [
    ptax !== null ? `PTAX ${ptax.toLocaleString('pt-BR')}` : null,
    desconto !== null ? `desconto AWS ${formatarPercentual(desconto)}` : null,
  ]
    .filter(Boolean)
    .join(' · ')
}

/**
 * As variáveis de um contrato novo, já com as três que a fórmula padrão usa.
 * Partir do padrão erra menos que partir do vazio; PTAX e desconto nascem sem
 * valor e precisam ser digitados.
 */
const VARIAVEIS_DE_CONTRATO_NOVO: VariavelDeContrato[] = [
  {
    tag: 'consumo_usd',
    nome: 'Consumo USD',
    descricao: 'Consumo AWS do período em dólares — fornecido pelo servidor no cálculo.',
    valor: null,
  },
  {
    tag: 'cotacao_ptax',
    nome: 'PTAX',
    descricao: 'Cotação PTAX pactuada — digitada, nunca consultada na internet.',
    valor: null,
  },
  {
    tag: 'desconto_aws',
    nome: 'Desconto AWS (%)',
    descricao: 'Desconto AWS pactuado, em pontos percentuais.',
    valor: null,
  },
]
const FORMULA_DE_CONTRATO_NOVO = '(consumo_usd * cotacao_ptax) * (1 - desconto_aws / 100)'

export function Contratos() {
  const idCliente = useClienteAtual()
  const { editar } = usePermission('orcamento.contratos')

  const consulta = useContratos(idCliente)
  const criar = useCriarContrato(idCliente)

  const [idSelecionado, setIdSelecionado] = useState<string | undefined>()
  const [criando, setCriando] = useState(false)

  const contratos = consulta.data ?? []
  // O vigente primeiro: é o que alguém abre a tela para ver. Sem isso, a seleção
  // padrão cairia no contrato mais antigo por acidente de ordenação.
  const selecionado =
    contratos.find((c) => c.idContrato === idSelecionado) ??
    contratos.find((c) => vigente(c)) ??
    contratos[0]

  if (consulta.isPending) {
    // A mesma grade mestre-detalhe do `return` lá embaixo: a coluna de
    // contratos e o painel do selecionado já nascem no lugar definitivo.
    return (
      <RegiaoDeCarregamento
        rotulo="Carregando contratos…"
        className="grid gap-4 xl:grid-cols-[minmax(0,22rem)_1fr]"
      >
        <EsqueletoDeLista variante="cartoes" itens={3} />
        <EsqueletoDeFormulario linhas={3} />
      </RegiaoDeCarregamento>
    )
  }
  if (consulta.isError) {
    return <EstadoDeErro erro={consulta.error} aoTentarNovamente={() => void consulta.refetch()} />
  }
  if (contratos.length === 0 && !criando) {
    return (
      <EstadoVazio
        titulo="Nenhum contrato cadastrado para este cliente."
        descricao="O orçamento e a reconciliação de faturas dependem de um contrato com CSBs, PTAX e desconto pactuados."
        acao={
          editar ? (
            <Botao variante="accent" icone="plus" onClick={() => setCriando(true)}>
              novo contrato
            </Botao>
          ) : undefined
        }
      />
    )
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="type-h4 text-text-primary">Contratos &amp; aditivos</h2>
        {editar && (
          <Botao variante="accent" icone="plus" onClick={() => setCriando((c) => !c)}>
            novo contrato
          </Botao>
        )}
      </div>

      {criando && (
        <FormularioDeContrato
          pendente={criar.isPending}
          erro={criar.isError ? criar.error : null}
          aoEnviar={(campos) => criar.mutate(campos, { onSuccess: () => setCriando(false) })}
          aoCancelar={() => setCriando(false)}
        />
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,22rem)_1fr]">
        <div className="flex flex-col gap-3">
          <ul className="flex flex-col gap-3">
            {contratos.map((contrato) => {
              const ehSelecionado = contrato.idContrato === selecionado?.idContrato
              const estaVigente = vigente(contrato)
              return (
                <li key={contrato.idContrato}>
                  <button
                    type="button"
                    aria-current={ehSelecionado}
                    onClick={() => setIdSelecionado(contrato.idContrato)}
                    className={`rounded-card w-full border p-4 text-left duration-fast ${
                      ehSelecionado
                        ? 'border-iox-navy bg-surface-card'
                        : 'border-border-subtle bg-surface-card hover:border-border-strong'
                    }`}
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="type-body-lg text-text-primary font-semibold">
                        Contrato {contrato.idContrato} — {contrato.broker}
                      </span>
                      <Badge tom={estaVigente ? 'sucesso' : 'neutro'}>
                        {estaVigente ? 'vigente' : 'encerrado'}
                      </Badge>
                    </span>
                    <span className="text-text-body mt-1.5 block type-sm">
                      {contrato.quantidadeCsbs.toLocaleString('pt-BR')} CSBs ·{' '}
                      {formatarData(contrato.vigenciaInicio)} – {formatarData(contrato.vigenciaFim)}
                    </span>
                    {resumoDeConstantes(contrato.variaveis) && (
                      <span className="text-text-muted mt-0.5 block type-sm">
                        {resumoDeConstantes(contrato.variaveis)}
                      </span>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>

          <p className="text-text-muted type-sm">
            Um cliente pode ter mais de um contrato de nuvem; o histórico é preservado como base de
            cálculo dos períodos passados.
          </p>
        </div>

        {selecionado && (
          /**
           * 🔑 `key` = o contrato. É o que reinicia o rascunho ao trocar de
           * contrato, sem `useEffect` e sem `setState` durante efeito.
           *
           * Sem isso, os campos digitados de um contrato ficariam por cima dos do
           * outro — o jeito mais silencioso possível de salvar a PTAX errada no
           * contrato errado.
           */
          <PainelDeParametros
            key={selecionado.idContrato}
            contrato={selecionado}
            idCliente={idCliente}
            editar={editar}
          />
        )}
      </div>
    </section>
  )
}

function PainelDeParametros({
  contrato,
  idCliente,
  editar,
}: {
  contrato: Contrato
  idCliente: string
  editar: boolean
}) {
  const aditivosDoContrato = useAditivos(idCliente, contrato.idContrato)
  const salvar = useSalvarContrato(idCliente)
  const criarAditivo = useCriarAditivo(idCliente)

  const [rascunho, setRascunho] = useState<Rascunho>(() => paraRascunho(contrato))
  const [parametros, setParametros] = useState<EstadoDeParametros>(() =>
    estadoInicial(contrato.variaveis, contrato.formulaCalculo),
  )
  const [erroDeParametros, setErroDeParametros] = useState<string | null>(null)
  const [anexando, setAnexando] = useState(false)

  const campo = (chave: keyof Rascunho) => ({
    value: rascunho[chave],
    disabled: !editar,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setRascunho((r) => ({ ...r, [chave]: e.target.value })),
  })

  return (
    <div className="border-border-subtle bg-surface-card rounded-card border p-5">
      <h3 className="type-h4 text-text-primary">Contrato {contrato.idContrato} — parâmetros</h3>

      <form
        className="mt-4 grid gap-4 md:grid-cols-3"
        onSubmit={(evento) => {
          evento.preventDefault()
          const resolvidos = resolverParametros(parametros)
          if (!resolvidos.ok) {
            setErroDeParametros(resolvidos.mensagem)
            return
          }
          setErroDeParametros(null)
          salvar.mutate({
            idContrato: contrato.idContrato,
            campos: {
              broker: rascunho.broker,
              quantidadeCsbs: Number(rascunho.quantidadeCsbs),
              vigenciaInicio: rascunho.vigenciaInicio,
              vigenciaFim: rascunho.vigenciaFim,
              variaveis: resolvidos.variaveis,
              formulaCalculo: resolvidos.formulaCalculo,
              mesFiscalDiaInicio: Number(rascunho.mesFiscalDiaInicio),
            },
          })
        }}
      >
        <Campo rotulo="ID do contrato">
          {/* Sempre desabilitado, mesmo para quem edita: o id é do servidor, e
              trocá-lo seria criar outro contrato. */}
          <Entrada value={contrato.idContrato} disabled readOnly />
        </Campo>

        <Campo rotulo="Broker (descritivo)">
          <Entrada {...campo('broker')} />
        </Campo>

        <Campo rotulo="Qtde original de CSBs" hint="unidade de consumo em nuvem">
          <Entrada inputMode="numeric" {...campo('quantidadeCsbs')} />
        </Campo>

        <Campo rotulo="Vigência — início">
          <Entrada type="date" {...campo('vigenciaInicio')} />
        </Campo>

        <Campo rotulo="Vigência — fim">
          <Entrada type="date" {...campo('vigenciaFim')} />
        </Campo>

        <Campo rotulo="Mês fiscal" hint="dia de início do ciclo do broker">
          <Entrada inputMode="numeric" {...campo('mesFiscalDiaInicio')} />
        </Campo>

        {/* 🔒 PTAX e desconto viraram linhas daqui; BR-04 segue escrito no subtítulo
            da seção. Não existe botão de consulta, e não deve passar a existir. */}
        <EditorDeParametros
          contexto="contrato"
          estado={parametros}
          aoAlterar={(proximo) => {
            setParametros(proximo)
            setErroDeParametros(null)
          }}
          editar={editar}
          erro={erroDeParametros}
        />

        {editar && (
          <div className="md:col-span-3 flex flex-wrap items-center gap-3">
            <Botao variante="primaria" type="submit" disabled={salvar.isPending}>
              {salvar.isPending ? 'salvando…' : 'salvar parâmetros'}
            </Botao>
            <span className="text-text-muted type-sm">
              corrige o que foi digitado errado — para repactuar, anexe um aditivo
            </span>
          </div>
        )}

        {salvar.isError && (
          <div className="md:col-span-3">
            <EstadoDeErro erro={salvar.error} />
          </div>
        )}
      </form>

      {/* ⚠️ §9 — quais campos o Serpro abre nunca foi especificado. Melhor dizer
          que falta do que preencher com um palpite. */}
      {/^serpro$/i.test(contrato.broker) && (
        <p className="rounded-card border-border-subtle bg-gray-50 text-text-muted mt-4 border p-3 type-sm">
          <i className="ph ph-info text-iox-navy mr-1.5" aria-hidden="true" />
          Contratos com broker <strong>Serpro</strong> habilitam campos adicionais que a iOX ainda
          não especificou. Eles não foram construídos — serão adicionados quando a definição chegar.
        </p>
      )}

      <div className="border-border-subtle mt-6 border-t pt-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h4 className="type-body-lg text-text-primary font-semibold">Aditivos do contrato</h4>
          {editar && (
            <Botao variante="secundaria" icone="paperclip" onClick={() => setAnexando((a) => !a)}>
              anexar aditivo
            </Botao>
          )}
        </div>

        {anexando && (
          <FormularioDeAditivo
            pendente={criarAditivo.isPending}
            erro={criarAditivo.isError ? criarAditivo.error : null}
            quantidadeCsbs={rascunho.quantidadeCsbs}
            // 🔒 Pré-preenchido com as variáveis e a fórmula SALVAS do contrato.
            variaveis={contrato.variaveis}
            formulaCalculo={contrato.formulaCalculo}
            aoEnviar={(campos) =>
              criarAditivo.mutate(
                { idContrato: contrato.idContrato, campos },
                { onSuccess: () => setAnexando(false) },
              )
            }
          />
        )}

        {aditivosDoContrato.isPending && (
          <EsqueletoDeLista
            rotulo="Carregando aditivos…"
            variante="cartoes"
            itens={2}
            className="mt-3"
          />
        )}

        {aditivosDoContrato.data && aditivosDoContrato.data.length === 0 && (
          <p className="text-text-muted mt-3 type-sm">
            Nenhum aditivo. Os parâmetros acima valem por toda a vigência.
          </p>
        )}

        <ul className="mt-3 flex flex-col gap-2">
          {/* Ordem do SERVIDOR — cronológica. Ver o cabeçalho do arquivo. */}
          {aditivosDoContrato.data?.map((aditivo, indice) => (
            <li
              key={aditivo.idAditivo}
              className="rounded-card border-border-subtle flex items-start gap-3 border p-3"
            >
              <i className="ph ph-file-plus text-iox-navy mt-0.5 text-xl" aria-hidden="true" />
              <div className="min-w-0">
                <p className="text-text-primary type-sm">
                  <strong className="font-semibold">
                    Aditivo {String(indice + 1).padStart(2, '0')}
                  </strong>{' '}
                  — {aditivo.quantidadeCsbs.toLocaleString('pt-BR')} CSBs
                  {/* PTAX e desconto lidos das variáveis do ADITIVO, pela tag. */}
                  {resumoDeConstantes(aditivo.variaveis) &&
                    ` · ${resumoDeConstantes(aditivo.variaveis)}`}
                </p>
                <p className="text-text-muted type-sm">
                  vigente a partir de {formatarData(aditivo.vigenteAPartirDe)} · parâmetros
                  anteriores mantidos como base de cálculo retroativa
                </p>
              </div>
            </li>
          ))}
        </ul>

        <p className="text-text-muted mt-4 type-sm">
          O aditivo redefine todos os parâmetros e passa a valer{' '}
          <strong className="text-text-body font-semibold">da data do aditivo em diante</strong>; o
          passado continua calculado pelos valores pactuados à época.
        </p>
      </div>
    </div>
  )
}

/**
 * O formulário de aditivo.
 *
 * 🔒 Nasce PRÉ-PREENCHIDO com os parâmetros vigentes, e não vazio. Um aditivo
 * redefine TODOS os parâmetros — quem repactua só a PTAX precisa que os outros
 * continuem valendo, e um formulário em branco faria "não mexi neste campo"
 * virar "zerei este campo".
 */
function FormularioDeAditivo({
  quantidadeCsbs,
  variaveis,
  formulaCalculo,
  pendente,
  erro,
  aoEnviar,
}: {
  quantidadeCsbs: string
  variaveis: VariavelDeContrato[]
  formulaCalculo: string
  pendente: boolean
  erro: unknown
  aoEnviar: (campos: Record<string, unknown>) => void
}) {
  const [vigenteAPartirDe, setVigenteAPartirDe] = useState('')
  const [csbs, setCsbs] = useState(quantidadeCsbs)
  // 🔒 BR-21 — o aditivo carrega o PRÓPRIO conjunto de variáveis e fórmula.
  const [parametros, setParametros] = useState<EstadoDeParametros>(() =>
    estadoInicial(variaveis, formulaCalculo),
  )
  const [erroDeParametros, setErroDeParametros] = useState<string | null>(null)

  return (
    <form
      className="rounded-card border-border-subtle bg-gray-50 mt-3 grid gap-4 border p-4 md:grid-cols-4"
      onSubmit={(evento) => {
        evento.preventDefault()
        const resolvidos = resolverParametros(parametros)
        if (!resolvidos.ok) {
          setErroDeParametros(resolvidos.mensagem)
          return
        }
        setErroDeParametros(null)
        aoEnviar({
          vigenteAPartirDe,
          quantidadeCsbs: Number(csbs),
          variaveis: resolvidos.variaveis,
          formulaCalculo: resolvidos.formulaCalculo,
        })
      }}
    >
      <Campo rotulo="Vigente a partir de" obrigatorio hint="os períodos anteriores não mudam">
        <Entrada
          type="date"
          required
          value={vigenteAPartirDe}
          onChange={(e) => setVigenteAPartirDe(e.target.value)}
        />
      </Campo>
      <Campo rotulo="Qtde de CSBs">
        <Entrada inputMode="numeric" value={csbs} onChange={(e) => setCsbs(e.target.value)} />
      </Campo>

      <EditorDeParametros
        contexto="aditivo"
        estado={parametros}
        aoAlterar={(proximo) => {
          setParametros(proximo)
          setErroDeParametros(null)
        }}
        editar
        erro={erroDeParametros}
      />

      <div className="md:col-span-4">
        <Botao variante="primaria" type="submit" disabled={pendente}>
          {pendente ? 'anexando…' : 'anexar aditivo'}
        </Botao>
      </div>

      {erro != null && (
        <div className="md:col-span-4">
          <EstadoDeErro erro={erro} />
        </div>
      )}
    </form>
  )
}

/**
 * O formulário de `novo contrato`.
 *
 * Todos os campos que ele pede já existem no contrato §7.2 — não há nenhuma
 * lacuna do §9 aqui, e por isso ele pôde ser construído inteiro. O que continua
 * em aberto são os campos EXTRAS que o broker Serpro habilita, e esses não são
 * pedidos nem aqui nem no painel de parâmetros.
 */
function FormularioDeContrato({
  pendente,
  erro,
  aoEnviar,
  aoCancelar,
}: {
  pendente: boolean
  erro: unknown
  aoEnviar: (campos: Record<string, unknown>) => void
  aoCancelar: () => void
}) {
  const [campos, setCampos] = useState({
    idContrato: '',
    broker: '',
    quantidadeCsbs: '',
    vigenciaInicio: '',
    vigenciaFim: '',
    mesFiscalDiaInicio: '1',
  })
  // Pré-preenchidas com a fórmula que os contratos existentes usam.
  const [parametros, setParametros] = useState<EstadoDeParametros>(() =>
    estadoInicial(VARIAVEIS_DE_CONTRATO_NOVO, FORMULA_DE_CONTRATO_NOVO),
  )
  const [erroDeParametros, setErroDeParametros] = useState<string | null>(null)

  const alterar = (chave: keyof typeof campos) => ({
    value: campos[chave],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
      setCampos((c) => ({ ...c, [chave]: e.target.value })),
  })

  return (
    <form
      className="rounded-card border-border-subtle bg-surface-card grid gap-4 border p-5 md:grid-cols-3"
      onSubmit={(evento) => {
        evento.preventDefault()
        const resolvidos = resolverParametros(parametros)
        if (!resolvidos.ok) {
          setErroDeParametros(resolvidos.mensagem)
          return
        }
        setErroDeParametros(null)
        aoEnviar({
          idContrato: campos.idContrato,
          broker: campos.broker,
          quantidadeCsbs: Number(campos.quantidadeCsbs),
          vigenciaInicio: campos.vigenciaInicio,
          vigenciaFim: campos.vigenciaFim,
          variaveis: resolvidos.variaveis,
          formulaCalculo: resolvidos.formulaCalculo,
          mesFiscalDiaInicio: Number(campos.mesFiscalDiaInicio),
        })
      }}
    >
      <h3 className="type-body-lg text-text-primary font-semibold md:col-span-3">Novo contrato</h3>

      <Campo rotulo="ID do contrato" obrigatorio hint="como consta no instrumento, ex. 042/2024">
        <Entrada required placeholder="042/2024" {...alterar('idContrato')} />
      </Campo>
      <Campo rotulo="Broker (descritivo)" obrigatorio>
        <Entrada required placeholder="SERPRO" {...alterar('broker')} />
      </Campo>
      <Campo rotulo="Qtde original de CSBs" hint="unidade de consumo em nuvem">
        <Entrada inputMode="numeric" {...alterar('quantidadeCsbs')} />
      </Campo>

      <Campo rotulo="Vigência — início" obrigatorio>
        <Entrada type="date" required {...alterar('vigenciaInicio')} />
      </Campo>
      <Campo rotulo="Vigência — fim" obrigatorio>
        <Entrada type="date" required {...alterar('vigenciaFim')} />
      </Campo>
      <Campo rotulo="Mês fiscal" hint="dia de início do ciclo do broker">
        <Entrada inputMode="numeric" {...alterar('mesFiscalDiaInicio')} />
      </Campo>

      <EditorDeParametros
        contexto="novo contrato"
        estado={parametros}
        aoAlterar={(proximo) => {
          setParametros(proximo)
          setErroDeParametros(null)
        }}
        editar
        erro={erroDeParametros}
      />

      <div className="md:col-span-3 flex items-center gap-3">
        <Botao variante="primaria" type="submit" disabled={pendente}>
          {pendente ? 'salvando…' : 'salvar contrato'}
        </Botao>
        <Botao variante="discreta" onClick={aoCancelar}>
          cancelar
        </Botao>
      </div>

      {erro != null && (
        <div className="md:col-span-3">
          <EstadoDeErro erro={erro} />
        </div>
      )}
    </form>
  )
}
